import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { ModuleLayout } from './ModuleLayout';
import { PlaceholderPage } from './PlaceholderPage';
import { LoginPage, NoAccessPage, NotFoundPage } from './StatusPages';
import { MODULE_DEFINITIONS, landingRoute } from './navigation';
import { accessibleModules, activeRoleIn, useSession } from '../auth/SessionContext';
import { normalizeRole } from './roles';

/** Sends the user to the first module they actually have access to. */
function HomeRedirect() {
  const session = useSession();
  const available = accessibleModules(session);

  if (available.length === 0) return <Navigate to="/no-access" replace />;

  const first = available[0]!;
  const target = landingRoute(first, activeRoleIn(session, first));
  return <Navigate to={target ?? '/no-access'} replace />;
}

/** /certifications -> /certifications/<active role>/<first section> */
function ModuleIndexRedirect() {
  const { module } = useParams<{ module: string }>();
  const session = useSession();
  const key = MODULE_DEFINITIONS.find(m => m.key === module)?.key;
  // An unknown module is a 404, not a silent bounce to the default module.
  if (!key) return <NotFoundPage />;
  const target = landingRoute(key, activeRoleIn(session, key));
  return <Navigate to={target ?? `/no-access/${key}`} replace />;
}

/** /certifications/manager -> /certifications/manager/dashboard */
function RoleIndexRedirect() {
  const { module, role } = useParams<{ module: string; role: string }>();
  const key = MODULE_DEFINITIONS.find(m => m.key === module)?.key;
  if (!key) return <NotFoundPage />;
  const normalized = role ? normalizeRole(role) : null;
  const target = normalized ? landingRoute(key, normalized) : null;
  return <Navigate to={target ?? `/no-access/${key}`} replace />;
}

/**
 * Route table.
 *
 * Every in-module path resolves to PlaceholderPage for now. The module phases
 * replace these with real screens without touching the shell or the nav map.
 */
export function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<HomeRedirect />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/no-access" element={<NoAccessPage />} />
      <Route path="/no-access/:module" element={<NoAccessPage />} />

      {/* The role is part of the path so ModuleLayout can both resolve the
          correct navigation and check the user actually holds that role. */}
      <Route path="/:module" element={<ModuleIndexRedirect />} />
      <Route path="/:module/:role" element={<ModuleLayout />}>
        <Route index element={<RoleIndexRedirect />} />
        <Route path=":section" element={<PlaceholderPage />} />
        <Route path=":section/*" element={<PlaceholderPage />} />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
